// Contenu curé (FR) — Conseils & Coaching V1. Articles fixes, fiables et hors-ligne.

export const PILLARS = [
  {
    id: "legal",
    icon: "scale",
    color: "#1B4332",
    title: "Légal & Formalisation",
    subtitle: "Passer de l'informel au formel, en règle",
    articles: [
      {
        id: "legal-formaliser",
        title: "De l'informel au formel : par où commencer ?",
        teaser: "Le parcours pas à pas pour enregistrer officiellement votre activité.",
        sections: [
          { text: "Formaliser, c'est donner une existence légale à votre commerce. Cela vous ouvre l'accès aux financements, aux grands clients et vous protège juridiquement. Voici les étapes essentielles." },
          {
            heading: "Les étapes clés",
            bullets: [
              "Choisir un statut adapté : entreprise individuelle (le plus simple pour démarrer seul) ou société.",
              "Obtenir votre registre du commerce (RCCM) auprès du guichet unique de création d'entreprise (ex. CEPICI en Côte d'Ivoire).",
              "Demander votre numéro de contribuable / identifiant fiscal (DGI).",
              "Ouvrir un compte professionnel (banque ou Mobile Money marchand) séparé de vos finances personnelles.",
              "Conserver toutes vos factures d'achat et de vente dès le premier jour.",
            ],
          },
          {
            heading: "Combien ça coûte ?",
            text: "Les frais d'enregistrement d'une entreprise individuelle sont volontairement faibles dans la plupart des pays d'Afrique de l'Ouest, et les guichets uniques permettent souvent de tout faire en quelques jours. Renseignez-vous sur les programmes d'accompagnement gratuits près de chez vous.",
          },
        ],
        tip: "Commencez en entreprise individuelle : c'est rapide, peu coûteux et vous pourrez évoluer en société plus tard quand votre activité grandit.",
      },
      {
        id: "legal-toolbox",
        title: "La boîte à outils juridique",
        teaser: "Contrats types, droits et obligations de l'entrepreneur.",
        sections: [
          {
            heading: "Les documents à toujours avoir",
            bullets: [
              "Un bon de commande ou facture pour chaque vente (nom, quantité, prix, date).",
              "Un modèle de contrat simple pour vos fournisseurs et vos livreurs.",
              "Des conditions de vente claires : délais, retours, garanties.",
              "Un registre des entrées/sorties d'argent (votre tableau de bord SuccèsNet le fait pour vous).",
            ],
          },
          {
            heading: "Vos obligations principales",
            bullets: [
              "Émettre des factures et déclarer votre activité.",
              "Respecter les délais de paiement convenus avec vos fournisseurs.",
              "Garantir la conformité des produits vendus (qualité, sécurité).",
            ],
          },
          {
            heading: "Vos droits",
            bullets: [
              "Être payé pour vos ventes et recouvrer vos créances.",
              "Protéger votre nom commercial et votre logo.",
              "Accéder aux financements et aux marchés publics une fois formalisé.",
            ],
          },
        ],
        tip: "Un simple contrat écrit, même court, vaut mieux qu'un long accord verbal. Mettez tout par écrit, signé des deux parties.",
      },
      {
        id: "legal-compta",
        title: "Pourquoi une comptabilité propre change tout",
        teaser: "La base de la pérennité et de la crédibilité de votre entreprise.",
        sections: [
          { text: "Une comptabilité claire n'est pas qu'une obligation : c'est votre meilleur outil de pilotage et votre passeport pour le crédit." },
          {
            heading: "Ce que ça vous apporte",
            bullets: [
              "Savoir si vous gagnez vraiment de l'argent (bénéfice net, pas seulement le chiffre d'affaires).",
              "Convaincre une banque ou un investisseur avec des chiffres fiables.",
              "Anticiper les périodes creuses et éviter les ruptures de trésorerie.",
              "Fixer des prix justes en connaissant votre coût de revient réel.",
            ],
          },
          {
            heading: "La règle d'or",
            text: "Ne mélangez jamais l'argent de l'entreprise et l'argent personnel. Versez-vous un « salaire » fixe et laissez le reste dans l'activité.",
          },
        ],
        tip: "Votre tableau de bord SuccèsNet calcule déjà votre bénéfice net, votre coût de revient et la valeur de votre stock : utilisez-le chaque semaine.",
      },
    ],
  },
  {
    id: "finance",
    icon: "landmark",
    color: "#E85D04",
    title: "Financement & Crédit",
    subtitle: "Préparer, comprendre et trouver de l'argent",
    articles: [
      {
        id: "fin-dossier",
        title: "Préparer un dossier de prêt convaincant",
        teaser: "Comment présenter vos chiffres pour obtenir un financement.",
        sections: [
          { text: "Un financeur prête à ceux qui inspirent confiance. Votre dossier doit raconter une histoire claire, chiffrée et réaliste." },
          {
            heading: "Ce qu'un financeur veut voir",
            bullets: [
              "Votre chiffre d'affaires des 6 à 12 derniers mois (régularité = confiance).",
              "Votre bénéfice net : prouvez que l'activité est rentable.",
              "À quoi servira l'argent (stock, équipement, local) et combien il rapportera.",
              "Votre capacité de remboursement mensuel sans étouffer la trésorerie.",
              "Vos garanties éventuelles et votre apport personnel.",
            ],
          },
          {
            heading: "La présentation gagnante",
            text: "Exportez votre rapport financier depuis SuccèsNet, ajoutez une page expliquant votre projet et le montant demandé. Un dossier propre vous distingue immédiatement.",
          },
        ],
        tip: "Demandez un montant précis et justifié. « J'ai besoin de 500 000 pour acheter X et vendre Y » est bien plus convaincant que « j'ai besoin d'argent ».",
      },
      {
        id: "fin-education",
        title: "Chiffre d'affaires vs bénéfice net",
        teaser: "Comprendre vos chiffres et gérer votre trésorerie.",
        sections: [
          {
            heading: "La différence essentielle",
            bullets: [
              "Chiffre d'affaires = tout l'argent encaissé par vos ventes.",
              "Coût de revient = ce que vos produits vous ont coûté (achat + transport + frais).",
              "Bénéfice net = ce qu'il reste réellement dans votre poche après TOUTES les dépenses.",
            ],
          },
          { heading: "Pourquoi c'est vital", text: "Beaucoup d'entrepreneurs vendent beaucoup mais ne gagnent rien, car ils confondent l'argent qui entre avec l'argent qui reste. Surveillez le bénéfice net, pas seulement les ventes." },
          {
            heading: "Gérer sa trésorerie",
            bullets: [
              "Gardez toujours une réserve pour 1 à 2 mois de dépenses.",
              "Ne réinvestissez pas 100 % de vos gains d'un coup.",
              "Séparez l'argent du stock, de vos charges et de votre rémunération.",
            ],
          },
        ],
        tip: "Être éligible à un crédit, c'est montrer un bénéfice net régulier et une trésorerie saine, mois après mois.",
      },
      {
        id: "fin-aides",
        title: "Où trouver un financement ?",
        teaser: "Microfinance, programmes et accompagnement des jeunes entreprises.",
        sections: [
          {
            heading: "Les pistes à explorer",
            bullets: [
              "Institutions de microfinance (IMF) : prêts adaptés aux petits commerces.",
              "Programmes publics d'appui à l'entrepreneuriat et aux jeunes (Agence Emploi Jeunes, fonds nationaux).",
              "Tontines et groupes d'épargne solidaire pour démarrer sans banque.",
              "Incubateurs et ONG qui financent ou accompagnent les TPE.",
              "Mobile Money marchand : certains opérateurs proposent des micro-crédits selon votre historique.",
            ],
          },
          { heading: "Avant de vous engager", text: "Comparez toujours le taux d'intérêt, la durée et les pénalités. Un crédit trop cher peut couler une activité saine. Calculez si le bénéfice généré couvre largement le remboursement." },
        ],
        tip: "Construisez un historique : même un petit prêt bien remboursé augmente fortement votre crédibilité pour un financement plus important ensuite.",
      },
    ],
  },
  {
    id: "strategy",
    icon: "trending",
    color: "#0077B6",
    title: "Stratégie commerciale & Ventes",
    subtitle: "Vendre plus, mieux, et fidéliser",
    articles: [
      {
        id: "strat-vente",
        title: "Vendre, relancer, fidéliser",
        teaser: "Les techniques pour aborder, convaincre et garder vos clients.",
        sections: [
          {
            heading: "Aborder un client",
            bullets: [
              "Écoutez son besoin avant de proposer un produit.",
              "Mettez en avant le bénéfice (« cette robe vous ira parfaitement pour les fêtes ») plutôt que la simple description.",
              "Proposez 2 options pour faciliter la décision, pas 10.",
            ],
          },
          {
            heading: "Relancer sans déranger",
            bullets: [
              "Un message WhatsApp court 2-3 jours après une visite sans achat.",
              "Annoncez une nouveauté ou une offre limitée dans le temps.",
              "Remerciez toujours, même sans achat : la relation prime.",
            ],
          },
          {
            heading: "Fidéliser",
            bullets: [
              "Notez les préférences de vos bons clients.",
              "Offrez un petit avantage au 3e ou 5e achat.",
              "Demandez un avis après livraison (SuccèsNet le fait automatiquement).",
            ],
          },
        ],
        tip: "Garder un client coûte 5 fois moins cher que d'en trouver un nouveau. Soignez l'après-vente autant que la vente.",
      },
      {
        id: "strat-marketing",
        title: "Le marketing digital simplifié",
        teaser: "Faire connaître vos produits sur WhatsApp, Facebook et TikTok.",
        sections: [
          {
            heading: "WhatsApp Business",
            bullets: [
              "Créez un catalogue et un statut quotidien avec vos nouveautés.",
              "Partagez le lien de votre boutique SuccèsNet à chaque conversation.",
              "Répondez vite : la rapidité fait vendre.",
            ],
          },
          {
            heading: "Facebook & Instagram",
            bullets: [
              "Publiez des photos nettes et lumineuses (la qualité photo vend).",
              "Racontez l'histoire de vos produits, pas seulement le prix.",
              "Rejoignez les groupes locaux d'achat/vente de votre ville.",
            ],
          },
          {
            heading: "TikTok",
            bullets: [
              "Filmez des vidéos courtes : déballage, essayage, coulisses.",
              "Restez régulier : 3 vidéos par semaine valent mieux qu'une vidéo parfaite par mois.",
              "Ajoutez toujours un appel à l'action : « commandez via le lien ».",
            ],
          },
        ],
        tip: "Utilisez les photos « packshot » et les aperçus mannequins IA de SuccèsNet pour des visuels pro qui attirent l'œil sur les réseaux.",
      },
      {
        id: "strat-produits",
        title: "Identifier vos best-sellers",
        teaser: "Miser sur ce qui rapporte, éliminer ce qui coûte.",
        sections: [
          { text: "Tous vos produits ne se valent pas. Certains tournent vite et rapportent ; d'autres dorment en stock et immobilisent votre argent." },
          {
            heading: "Repérer vos champions",
            bullets: [
              "Regardez le classement « Top des ventes » de votre boutique SuccèsNet.",
              "Calculez la marge réelle par produit, pas seulement les quantités vendues.",
              "Renforcez le stock des produits qui se vendent vite ET avec une bonne marge.",
            ],
          },
          {
            heading: "Éliminer les boulets",
            bullets: [
              "Un produit qui ne tourne pas en 2-3 mois immobilise votre trésorerie.",
              "Liquidez-le en promotion pour récupérer du cash.",
              "Évitez de recommander un produit qui ne se vend pas, même s'il vous plaît.",
            ],
          },
        ],
        tip: "Le bon réflexe : réinvestir l'argent des produits lents dans vos best-sellers. C'est ainsi qu'on accélère la croissance.",
      },
    ],
  },
];

export const QUIZ = [
  {
    id: "q_formal",
    question: "Votre activité est-elle formalisée (registre de commerce, identifiant fiscal) ?",
    options: [
      { label: "Oui, entièrement", score: 2 },
      { label: "En cours", score: 1 },
      { label: "Pas encore", score: 0 },
    ],
    reco: "Lancez votre formalisation : c'est souvent une condition pour obtenir un crédit (voir le pilier Légal & Formalisation).",
  },
  {
    id: "q_compta",
    question: "Tenez-vous un suivi régulier de vos ventes et dépenses ?",
    options: [
      { label: "Oui, chaque semaine", score: 2 },
      { label: "De temps en temps", score: 1 },
      { label: "Non", score: 0 },
    ],
    reco: "Suivez vos chiffres chaque semaine dans votre tableau de bord : un financeur exige un historique fiable.",
  },
  {
    id: "q_profit",
    question: "Connaissez-vous votre bénéfice net mensuel ?",
    options: [
      { label: "Oui, précisément", score: 2 },
      { label: "Approximativement", score: 1 },
      { label: "Non", score: 0 },
    ],
    reco: "Apprenez à distinguer chiffre d'affaires et bénéfice net (voir le pilier Financement & Crédit).",
  },
  {
    id: "q_regular",
    question: "Vos revenus sont-ils réguliers depuis au moins 6 mois ?",
    options: [
      { label: "Oui, stables ou en hausse", score: 2 },
      { label: "Variables", score: 1 },
      { label: "Activité récente (< 6 mois)", score: 0 },
    ],
    reco: "Construisez un historique d'au moins 6 mois de ventes régulières avant de solliciter un prêt important.",
  },
  {
    id: "q_account",
    question: "Avez-vous un compte (bancaire ou Mobile Money marchand) dédié à l'activité ?",
    options: [
      { label: "Oui, séparé du personnel", score: 2 },
      { label: "Je mélange un peu", score: 1 },
      { label: "Non", score: 0 },
    ],
    reco: "Ouvrez un compte dédié à l'entreprise : c'est la base d'une trésorerie saine et crédible.",
  },
  {
    id: "q_plan",
    question: "Savez-vous précisément à quoi servirait le financement ?",
    options: [
      { label: "Oui, avec un montant chiffré", score: 2 },
      { label: "Une idée générale", score: 1 },
      { label: "Pas vraiment", score: 0 },
    ],
    reco: "Définissez un objectif chiffré (stock, équipement…) et son retour attendu avant toute demande.",
  },
];
